import { useMemo, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';
import { bouwAandachtslijst, type AandachtsRegel, type Aandachtslijst as AandachtslijstGegevens } from '../aandachtslijst';
import { naamVoorRegel } from '../naam';
import { naamVoorCategorie } from '../productcategorieen';
import { bepaalZwaartepunt, type Zwaartepunt } from '../zwaartepunt';
import type { StapKeuze } from '../stapFilter';
import type { Normen } from '../normen';
import type { HandlingTimePerPersonRow } from '../handlingTime.api';

/**
 * Story 33.2 — de aandachtslijst: waar zit de tijd boven de norm.
 *
 * Het blok dat het scherm leidt. Per medewerker het verschil met de norm en wat dat verschil deze
 * periode kostte, gerangschikt op KOST — niet op snelheid en niet op totale minuten. Het rekenwerk
 * staat in `aandachtslijst.ts`; hier staat alleen hoe het op het scherm komt.
 *
 * Drie dingen die dit onderdeel met opzet NIET doet:
 *
 * - Het velt geen oordeel. Er staat geen groen, amber of rood: wie hier staat zit boven de norm, en
 *   dat is al gezegd door het feit dat hij er staat. De kleuren blijven bij de tabel en de kaarten.
 * - Het maakt van een ontbrekende norm geen nul en geen streepje. Zonder norm staat er één regel
 *   die zegt dat er nog geen norm is en waar die gezet wordt — geen foutmelding, geen
 *   waarschuwingskleur, want er is niets kapot (AC-7 en AC-8).
 * - Het telt de twee stappen nooit bij elkaar op. Op "Beide" staan er twee lijsten onder elkaar,
 *   elk tegen zijn eigen norm; binnen één stap is `tradeItems` een aantal stuks, over de stappen
 *   heen zou het een aantal behandelingen zijn (AC-1 en AC-5).
 *
 * DE BALK IS DECORATIE (AC-6). Elk getal dat hij toont staat er ook als tekst naast, en een
 * schermlezer krijgt uitsluitend die tekst. Haal de balk weg en de lijst is nog steeds compleet.
 *
 * EN HET IS EEN ECHTE TABEL, met kolomkoppen die aan de cellen vastzitten. Dat is geen opmaakkeuze:
 * de eerste opzet was een raster van losse vakjes met `role="button"` en een `aria-label` op de
 * regel, en zo'n label VERVANGT de inhoud voor een schermlezer. Die kreeg dan "Grafiek beperken tot
 * Sita Kasi" te horen en geen van de vijf getallen waar dit blok over gaat — precies de vijf
 * gegevens die AC-1 belooft. In een tabel hoort de lezer bij elk getal de kolom waar het in staat.
 */

export interface AandachtslijstProps {
  /** De regels zoals het scherm ze toont — al beperkt tot de gekozen stap door `stapFilter`. */
  rijen: HandlingTimePerPersonRow[];
  /** Eén stap, of op "Beide" de twee stappen in hun vaste volgorde: vastleggen vóór valideren. */
  stappen: readonly StapKeuze[];
  /**
   * De twee normen (story 31.5), ALTIJD als object en nooit afwezig.
   *
   * Een lege norm is hier geen reden om het blok te verbergen: juist dán hoort er te staan dat er
   * nog niets is om tegen af te zetten, en waar dat gezet wordt (AC-7).
   */
  normen: Normen;
  /**
   * Of de bron de uitsplitsing per stap MIST — dan valt er niets per stap te rekenen.
   *
   * Codereviewbevinding 30 augustus 2026. Zonder dit meldde het blok "er is niemand die vastleggen
   * deed", terwijl het echte antwoord is dat er over niemand iets uitgesplitst is. Dat is een
   * onwaarheid over álle collega's tegelijk, en precies de stille verdwijning die story 33.1
   * wegneemt.
   */
  uitsplitsingMist?: boolean;
  /** De persoon waarop de grafiek gefilterd is, of `null`. Dezelfde keuze als in de tabel. */
  gekozenPersoon: number | null;
  /** `null` betekent "laat het filter los"; een tweede klik op dezelfde regel doet dat. */
  onKiesPersoon: (userId: number | null) => void;
}

const SLEUTEL = 'handlingTime.aandachtslijst';

/** Onder deze grens leest een kost als "minder dan een minuut" en niet als nul (AC-13). */
const HALVE_MINUUT = 0.5;

/**
 * Onder deze grens leest een verschil als "minder dan 0,1" en niet als "+0,0".
 *
 * Codereviewbevinding 30 augustus 2026: een verschil van 0,02 minuut per stuk stond op het scherm
 * als "+0,0" naast een kost van "minder dan een minuut". Dat is dezelfde nul die AC-13 bij de kost
 * verbiedt, één kolom verderop — en juist bij het getal dat zegt waaróm iemand in de lijst staat.
 */
const KLEINSTE_VERSCHIL = 0.05;

/**
 * Het aantal decimalen waarop een grens beslecht wordt, precies zoals in `normen.ts`.
 *
 * Zonder deze afronding beslist drijvendekommaruis de grens: een kost die precies een halve minuut
 * hoort te zijn komt uit de deling en de vermenigvuldiging als 0.4999999999999999 terug, en leest
 * dan als "minder dan een minuut" terwijl AC-13 die grens inclusief legt.
 */
const PRECISIE = 1e6;

/** Afronden op zes decimalen vóór een vergelijking, zodat de ruis niet de grens verschuift. */
function opPrecisie(waarde: number): number {
  return Math.round(waarde * PRECISIE) / PRECISIE;
}

/**
 * De vijf kolommen in hun vaste volgorde (AC-1).
 *
 * Eén lijst voor de koppen, zodat de kop en de cel eronder niet uit elkaar kunnen lopen.
 */
const KOLOMMEN = [
  { naam: 'persoon', rechts: false },
  { naam: 'gemiddelde', rechts: true },
  { naam: 'verschil', rechts: true },
  { naam: 'stuks', rechts: true },
  { naam: 'kost', rechts: true },
  // Story 34.1 — het zwaartepunt staat ACHTERAAN en verandert niets aan de rangschikking (AC-14).
  // Die blijft op kosten sorteren; dit is uitleg bij een naam die er al staat, en geen maatstaf.
  { naam: 'zwaartepunt', rechts: false },
] as const;

/** De markering van de gekozen regel, hier vastgelegd zodat hij niet als tekst in de lijst staat. */
const AANGEWEZEN = { 'aria-current': 'true' } as const;

export function Aandachtslijst({
  rijen,
  stappen,
  normen,
  uitsplitsingMist = false,
  gekozenPersoon,
  onKiesPersoon,
}: AandachtslijstProps) {
  const { t, i18n } = useTranslation();
  const taal = i18n.language;

  const heelGetal = useMemo(() => new Intl.NumberFormat(taal, { maximumFractionDigits: 0 }), [taal]);
  const eenDecimaal = useMemo(
    () => new Intl.NumberFormat(taal, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    [taal],
  );

  /** Eén lijst per stap, elk tegen de norm van díe stap. Nooit één lijst met twee normen (AC-5). */
  const lijsten = useMemo(
    () => stappen.map((stap) => ({ stap, lijst: bouwAandachtslijst(rijen, stap, normen[stap]) })),
    [normen, rijen, stappen],
  );

  const ergensEenLijst = lijsten.some(({ lijst }) => lijst.regels.length > 0);

  /**
   * Story 34.1 — de onbewerkte regel per medewerker, om het zwaartepunt uit te lezen.
   *
   * `AandachtsRegel` draagt hem met opzet niet: die staat vol met wat de RANGSCHIKKING nodig heeft,
   * en het zwaartepunt verandert daar niets aan (AC-14). Zou het erin zitten, dan was het een kwestie
   * van tijd voordat er op gesorteerd werd.
   */
  const rijenPerPersoon = useMemo(
    () => new Map(rijen.map((rij) => [rij.userId, rij])),
    [rijen],
  );

  /**
   * Het zwaartepunt als tekst, en de drie uitkomsten houden elk hun eigen zin.
   *
   * `gedeeld` is nadrukkelijk iets anders dan `geen`: bij het eerste is er wél werk maar deelt meer
   * dan één categorie het hoogste aantal (AC-13), bij het tweede valt er niets aan te wijzen omdat er
   * in deze stap niets is afgerond. Ze samenvouwen tot één streepje zou de lezer laten denken dat er
   * niets gebeurd is waar er juist een gelijkspel staat.
   */
  /**
   * Het zwaartepunt van deze medewerker in deze stap.
   *
   * Een regel die er niet is levert `geen` op en nooit een verzonnen categorie. Dat geval hoort niet
   * te bestaan — de lijst is uit dezelfde rijen gebouwd — maar het antwoord "hier valt niets aan te
   * wijzen" is het enige dat dan waar is.
   */
  function zwaartepuntVoor(userId: number, stap: StapKeuze): Zwaartepunt {
    const rij = rijenPerPersoon.get(userId);
    if (rij === undefined) return { soort: 'geen', totaal: 0 };
    return bepaalZwaartepunt(rij, stap);
  }

  function alsZwaartepunt(zwaartepunt: Zwaartepunt): string {
    // "De bron levert de uitsplitsing niet" is iets ANDERS dan "deze medewerker rondde niets af",
    // en dat verschil hoort de lezer te zien — codereviewbevinding 31 augustus 2026. Vielen ze
    // samen, dan stond er tijdens een uitrol "niets afgerond" naast élke naam.
    if (zwaartepunt.soort === 'onbekend') return t(`${SLEUTEL}.zwaartepuntOnbekend`);
    if (zwaartepunt.soort === 'geen') return t(`${SLEUTEL}.zwaartepuntGeen`);
    if (zwaartepunt.soort === 'gedeeld') {
      return t(`${SLEUTEL}.zwaartepuntGedeeld`, { totaal: heelGetal.format(zwaartepunt.totaal) });
    }
    return t(`${SLEUTEL}.zwaartepuntWaarde`, {
      categorie: zwaartepunt.code === null
        ? t(`${SLEUTEL}.zwaartepuntOverige`)
        : (naamVoorCategorie(zwaartepunt.code) ?? t(`${SLEUTEL}.zwaartepuntOverige`)),
      aantal: heelGetal.format(zwaartepunt.aantal),
      totaal: heelGetal.format(zwaartepunt.totaal),
    });
  }

  /**
   * Welke ondertitel er staat, en dat zijn DRIE gevallen en niet twee.
   *
   * Epic-brede review 30 augustus 2026: zonder uitsplitsing per stap viel het blok terug op
   * "Zodra er een norm staat, verschijnt de lijst hier" — terwijl de norm er wél stond en er dus
   * niets meer te wachten viel. Ontbreekt de uitsplitsing, dan is dát de boodschap, en dan belooft
   * de kop niets over een norm.
   */
  const ondertitel = uitsplitsingMist
    ? 'ondertitelZonderUitsplitsing'
    : (ergensEenLijst ? 'ondertitel' : 'ondertitelZonderLijst');

  /**
   * De kost in HELE minuten, en onder een halve minuut in woorden.
   *
   * Nul zou hier een onwaarheid zijn: wie in de lijst staat zit boven de norm, en dat kost dus tijd.
   * Een afronding die daar "0 min" van maakt spreekt de lijst tegen waar hij in staat (AC-13).
   */
  function alsKost(minuten: number): string {
    // DEZELFDE AFGERONDE WAARDE in de grens én in het getal, en dat is geen nettigheid: met de rauwe
    // waarde in de tweede regel leverde een kost die precies een halve minuut hoort te zijn
    // ("0.4999999999999999") de tekst "0 min" op — de nul die AC-13 nadrukkelijk verbiedt. Gemeten
    // op 30 augustus 2026 met 1,4 minuut over 3 stuks tegen een norm van 0,3.
    const afgerond = opPrecisie(minuten);
    if (afgerond < HALVE_MINUUT) return t(`${SLEUTEL}.kostKlein`);
    return t(`${SLEUTEL}.kostMinuten`, { waarde: heelGetal.format(Math.round(afgerond)) });
  }

  /**
   * Het verschil met de norm, en onder 0,05 in woorden.
   *
   * Om dezelfde reden als bij de kost: "+0,0" is een nul op het scherm, en wie in deze lijst staat
   * zit per definitie bóven de norm. Een getal dat het tegendeel suggereert is erger dan geen getal.
   */
  function alsVerschil(minuten: number): string {
    if (opPrecisie(minuten) < KLEINSTE_VERSCHIL) return t(`${SLEUTEL}.verschilKlein`);
    return t(`${SLEUTEL}.verschilWaarde`, { waarde: eenDecimaal.format(minuten) });
  }

  function kies(regel: AandachtsRegel) {
    if (!regel.aanklikbaar) return;
    // Nog een keer op dezelfde regel laat het filter los — precies zoals in de tabel (AC-18).
    onKiesPersoon(gekozenPersoon === regel.userId ? null : regel.userId);
  }

  /**
   * Enter en spatie doen hetzelfde als de muis.
   *
   * Op `keydown` en niet op `keyup`, en dat wijkt af van hoe een échte knop op de spatiebalk
   * reageert. Het is met opzet: de tabel eronder doet het sinds story 31.4 precies zo, en twee
   * regels in hetzelfde scherm die net iets anders op dezelfde toets reageren is verwarrender dan
   * één regel die van de norm afwijkt (codereviewbevinding 30 augustus 2026, afgewogen en niet
   * overgenomen).
   */
  function opToets(event: KeyboardEvent<HTMLTableRowElement>, regel: AandachtsRegel) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    kies(regel);
  }

  /**
   * Eén regel van de lijst, als tabelrij.
   *
   * De balk staat in de kolom die hij verbeeldt — het verschil met de norm — en loopt van nul tot
   * het HOOGSTE gemiddelde in deze lijst, met een streepje op de norm. Zit één persoon ver boven de
   * norm, dan vult die de balk en worden de andere balken klein; het GETAL ernaast blijft dan de
   * bron van waarheid, en dat is precies waarom de balk hier decoratie heet en niets draagt wat er
   * niet ook als tekst staat.
   *
   * De rij is aan te klikken zoals in de tabel eronder: nog een keer klikken laat het filter los, en
   * Enter of spatie doet hetzelfde als de muis (AC-18).
   */
  function regelBlok(stap: StapKeuze, regel: AandachtsRegel, norm: number, hoogste: number, gekozen: boolean) {
    const sleutel = `handling-time-aandachtslijst-${stap}-regel-${regel.userId}`;
    const deel = (waarde: number) => `${Math.min(100, Math.max(0, (waarde / hoogste) * 100))}%`;
    const bediening = t(`${SLEUTEL}.${gekozen ? 'kiesPersoonLos' : 'kiesPersoon'}`, { naam: naamVoorRegel(regel, t) });

    return (
      <TableRow
        {...(gekozen ? AANGEWEZEN : {})}
        {...(regel.aanklikbaar
          ? {
            onClick: () => kies(regel),
            onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => opToets(event, regel),
            tabIndex: 0,
            title: bediening,
          }
          : {})}
        className={`${regel.aanklikbaar ? 'cursor-pointer' : ''} ${gekozen ? 'bg-[var(--primary-faint)]' : ''}`}
        data-gekozen={gekozen ? 'true' : undefined}
        data-testid={sleutel}
        key={regel.userId}
      >
        <TableCell className="font-medium text-[var(--foreground)]" data-testid={`${sleutel}-naam`}>
          <span className={gekozen ? 'border-l-2 border-[var(--primary)] pl-2' : ''}>{naamVoorRegel(regel, t)}</span>
          {/* Wat een klik doet, alleen voor wie de rij niet ziet. Met de muis staat het in de
              tekstballon; met het toetsenbord hoort het hier, want een rij zonder deze zin is wel
              te bereiken maar zegt niet waarvoor. */}
          {regel.aanklikbaar && <span className="sr-only">{t(`${SLEUTEL}.bediening`, { bediening })}</span>}
        </TableCell>
        <TableCell className="text-right tabular-nums text-[var(--foreground-muted)]" data-testid={`${sleutel}-gemiddelde`}>
          {eenDecimaal.format(regel.gemiddeldePerStuk)}
        </TableCell>
        <TableCell className="min-w-[8rem] text-right tabular-nums text-[var(--foreground)]" data-testid={`${sleutel}-verschil`}>
          {alsVerschil(regel.bovenDeNorm)}
          {/* DE BALK, en verder niets: geen getal dat er niet ook als tekst boven staat, en voor een
              schermlezer helemaal niet aanwezig (AC-6). */}
          <span
            aria-hidden="true"
            className="mt-1 block h-1.5 w-full rounded-full bg-[var(--background-muted)]"
            data-testid={`${sleutel}-balk`}
          >
            <span className="relative block h-full">
              <span
                className="absolute inset-y-0 left-0 rounded-full bg-[var(--primary)]"
                style={{ width: deel(regel.gemiddeldePerStuk) }}
              />
              <span className="absolute inset-y-[-2px] w-px bg-[var(--foreground)]" style={{ left: deel(norm) }} />
            </span>
          </span>
        </TableCell>
        <TableCell className="text-right tabular-nums text-[var(--foreground-muted)]" data-testid={`${sleutel}-stuks`}>
          {heelGetal.format(regel.stuks)}
        </TableCell>
        <TableCell
          className="text-right font-semibold tabular-nums text-[var(--foreground-heading)]"
          data-testid={`${sleutel}-kost`}
        >
          {alsKost(regel.kost)}
        </TableCell>
        {/* HET ZWAARTEPUNT (story 34.1): waar het werk van deze persoon in DEZE stap viel. Het
            aantal ernaast is een ander getal dan de kolom `stuks`: daar staan onderscheiden
            producten, hier afgeronde behandelingen. Daarom draagt deze cel zijn eigen totaal mee. */}
        <TableCell
          className="whitespace-nowrap text-[var(--foreground-muted)]"
          data-testid={`${sleutel}-zwaartepunt`}
        >
          {alsZwaartepunt(zwaartepuntVoor(regel.userId, stap))}
        </TableCell>
      </TableRow>
    );
  }

  /** Eén stap: de kop, en daaronder de lijst óf de uitleg dat er nog geen norm is. */
  function stapBlok(stap: StapKeuze, lijst: AandachtslijstGegevens) {
    const sleutel = `handling-time-aandachtslijst-${stap}`;
    const hoogste = lijst.regels.reduce((meeste, regel) => Math.max(meeste, regel.gemiddeldePerStuk), 0);
    // Binnen de tak hieronder is de norm nooit `null` — dat is precies waar op getakt wordt. Hij
    // staat hier apart zodat de lijst geen terugval op nul nodig heeft die er nooit gebruikt wordt.
    const norm = lijst.norm ?? 0;

    return (
      <section className="mt-4 first:mt-0" data-testid={sleutel} key={stap}>
        {/* De kop staat er ALLEEN op "Beide". Op één gekozen stap zegt het filter bovenaan al waar
            dit blok over gaat, en een tweede keer hetzelfde woord is ruis. */}
        {stappen.length > 1 && (
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--foreground-muted)]">
            {t(`handlingTime.stapfilter.${stap}`)}
          </h3>
        )}

        {/* DE NORM ALS TEKST, en dat is de laatste helft van AC-6: het streepje in de balk is het
            enige getal dat alleen getekend werd, en een schermlezer kreeg het dus niet. Nu staat
            het er ook gewoon.

            MAAR NIET ZONDER UITSPLITSING (epic-brede review 30 augustus 2026). Deze regel stond hier
            onvoorwaardelijk, en dan las het blok als "Norm: 12,0 minuten per stuk." met daaronder
            "Zodra er een norm staat, verschijnt de lijst hier" — een norm die er is naast een belofte
            dat hij nog moet komen. Valt er niet tegen die norm te rekenen, dan is het getal noemen
            erger dan het weglaten. Sinds de tweede reviewronde wordt dit blok in dat geval helemaal
            niet meer getekend, dus die voorwaarde staat nu één niveau hoger. */}
        {lijst.norm !== null && (
          <p className="mt-1 text-xs text-[var(--foreground-muted)]" data-testid={`${sleutel}-norm`}>
            {t(`${SLEUTEL}.norm`, { waarde: eenDecimaal.format(lijst.norm) })}
          </p>
        )}

        {lijst.norm === null ? (
          /* GEEN FOUTMELDING EN GEEN WAARSCHUWINGSKLEUR (AC-8). Er is niets kapot; er is alleen nog
             niets om tegen af te zetten. Dezelfde letters en dezelfde kleur als elke andere
             toelichting op dit scherm. */
          <p className="mt-1 text-sm leading-relaxed text-[var(--foreground-muted)]" data-testid={`${sleutel}-geen-norm`}>
            {t(`${SLEUTEL}.geenNorm`, { stap: t(`handlingTime.stapfilter.${stap}`).toLowerCase() })}
          </p>
        ) : (
          <>
            {lijst.regels.length > 0 && (
              <div className="mt-2 overflow-x-auto">
                <Table data-testid={`${sleutel}-tabel`}>
                  <TableHeader>
                    <TableRow>
                      {KOLOMMEN.map(({ naam, rechts }) => (
                        <TableHead
                          className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[var(--foreground-muted)] ${
                            rechts ? 'text-right' : 'text-left'
                          }`}
                          key={naam}
                          scope="col"
                        >
                          {t(`${SLEUTEL}.${naam}`)}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lijst.regels.map((regel) =>
                      regelBlok(stap, regel, norm, hoogste, regel.aanklikbaar && gekozenPersoon === regel.userId))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* WIE ER NIET IN STAAT WORDT GETELD EN GENOEMD (AC-4 en AC-14), met per reden een eigen
                regel: op of onder de norm is iets anders dan geen stuk afgerond. Een lege lijst is
                dus geen leeg kader — er staat gewoon wat er wél gemeten is. */}
            {lijst.opOfOnderNorm > 0 && (
              <p className="mt-2 text-xs leading-relaxed text-[var(--foreground-muted)]" data-testid={`${sleutel}-buiten`}>
                {t(`${SLEUTEL}.opNorm`, { count: lijst.opOfOnderNorm })}
              </p>
            )}
            {lijst.zonderStuks > 0 && (
              <p className="mt-1 text-xs leading-relaxed text-[var(--foreground-muted)]" data-testid={`${sleutel}-zonder-stuks`}>
                {t(`${SLEUTEL}.zonderStuks`, { count: lijst.zonderStuks })}
              </p>
            )}
            {lijst.regels.length === 0 && lijst.opOfOnderNorm === 0 && lijst.zonderStuks === 0 && (
              <p className="mt-1 text-xs leading-relaxed text-[var(--foreground-muted)]" data-testid={`${sleutel}-niemand`}>
                {t(`${SLEUTEL}.niemand`, { stap: t(`handlingTime.stapfilter.${stap}`).toLowerCase() })}
              </p>
            )}
          </>
        )}
      </section>
    );
  }

  return (
    <Card className="p-5" data-testid="handling-time-aandachtslijst">
      <div>
        <h2 className="text-sm font-semibold text-[var(--foreground)]">{t(`${SLEUTEL}.titel`)}</h2>
        {/* DE BELOFTE VOLGT DE INHOUD, en dat is een codereviewbevinding van 30 augustus 2026: de
            ondertitel zei "gerangschikt op kosten, van hoog naar laag" ook wanneer er geen norm was
            en er dus niets onder stond. Zonder lijst hoort er te staan waar het blok vóór is, niet
            hoe een lijst gesorteerd zou zijn die er niet is (AC-7). */}
        <p className="mt-1 max-w-[46rem] text-xs text-[var(--foreground-muted)]" data-testid="handling-time-aandachtslijst-ondertitel">
          {t(`${SLEUTEL}.${ondertitel}`)}
        </p>
      </div>

      {/* ÉÉN BOODSCHAP, ÉÉN PLEK, en dat is een bevinding uit de tweede reviewronde van
          30 augustus 2026. Ontbreekt de uitsplitsing per stap, dan stond dezelfde mededeling in de
          ondertitel én daaronder nog eens per stapblok — op "Beide" dus drie keer onder elkaar. Zo'n
          herhaling leest als opvulling in plaats van als uitleg, precies zoals bij de
          eerlijkheidsnoot in de ronde ervoor.

          De stapblokken hebben zonder uitsplitsing niets anders te melden: er valt geen norm te
          noemen en geen lijst te tonen. Ze vervallen daarom in hun geheel, en de ondertitel draagt
          de volledige boodschap — wat er aan de hand is, wat dat betekent, en wanneer het blok
          terugkomt. */}
      {!uitsplitsingMist && lijsten.map(({ stap, lijst }) => stapBlok(stap, lijst))}

      {/* DE UITLEG ONDER DE LIJST (AC-3), en één keer voor het hele blok: op "Beide" staat dezelfde
          zin anders twee keer onder elkaar, en dan leest hij als opvulling in plaats van als uitleg. */}
      {ergensEenLijst && (
        <p
          className="-mx-5 mt-4 flex items-start gap-2 border-t border-[var(--border-soft)] bg-[var(--background-muted)] px-5 py-3 text-xs leading-relaxed text-[var(--foreground)]"
          data-testid="handling-time-aandachtslijst-uitleg"
        >
          <span aria-hidden="true" className="mdi mdi-scale-balance mt-px flex-none text-sm text-[var(--foreground-muted)]" />
          <span>
            {t(`${SLEUTEL}.uitleg`)}
            {' '}
            {/* DE EERLIJKHEIDSNOOT, en die hoort HIER te staan sinds story 33.2: de volledige tabel
                is ingeklapt, dus er leidt een lijst van namen het scherm zonder de regel die zegt
                dat een plek in zo'n lijst geen oordeel over iemand is.

                MAAR IN EIGEN WOORDEN, en dat is een bevinding uit de epic-brede review van
                30 augustus 2026. Hier stond letterlijk `handlingTime.table.eerlijkheid` — de zin van
                epic 31, over "een positie" en "lager in de lijst". Die tabel rangschikt op snelheid
                en heeft een positiekolom; deze lijst heeft geen positie en rangschikt op KOSTEN.
                Dezelfde zin overnemen las dus als een waarschuwing over iets wat hier niet staat, en
                stond bovendien woordelijk twee keer op het scherm zodra de tabel openging. */}
            {/* HIER STOND EEN ZIN DIE UITLEGDE WAAROM TWEE GETALLEN VERSCHILDEN, en die is met de
                correctie van AC-0 op 31 augustus 2026 vervallen: het zwaartepunt telt nu dezelfde
                stuks als de kolom ernaast. Een uitleg voor een verschil dat er niet meer is, is
                erger dan geen uitleg — hij zou de lezer laten zoeken naar iets wat er niet staat.
                Dat de twee gelijk BLIJVEN wordt bewaakt door `noemers.test.ts`, niet door een zin. */}
            {t(`${SLEUTEL}.eerlijkheid`)}
          </span>
        </p>
      )}
    </Card>
  );
}
